import React, { useState, useLayoutEffect, useRef, useId } from 'react'
import {
  FaUser,
  FaDollarSign,
  FaShieldAlt,
  FaBus,
  FaWheelchair,
  FaWifi,
  FaSun,
  FaLeaf,
  FaCloud,
  FaSnowflake,
  FaLanguage,
  FaWalking,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa'
import { GiCactus, GiMountainCave, GiGrass } from 'react-icons/gi'
import '../../../styles/components/destination_page/overview/SummaryPanel.css'

const RATING_ICONS = {
  crowd: { Icon: FaUser, label: 'Crowd Size' },
  cost: { Icon: FaDollarSign, label: 'Cost' },
  safety: { Icon: FaShieldAlt, label: 'Safety' },
  transport: { Icon: FaBus, label: 'Public Transport' },
  accessibility: { Icon: FaWheelchair, label: 'Accessibility' },
  wifi: { Icon: FaWifi, label: 'Wi-Fi Quality' },
  walkability: { Icon: FaWalking, label: 'Walkability' }
}

const RATING_DESCRIPTIONS = {
  crowd: { 1: 'Very quiet; ideal for peaceful exploration.', 2: 'Light crowds; rarely feels busy, easy to explore.', 3: 'Some crowds are common; popular spots fill up.', 4: 'Busy, especially at peak times and major sites.', 5: 'Very busy; expect queues and large crowds in many places.' },
  cost: { 1: 'Affordable, ideal for budget-conscious travellers.', 2: 'Generally inexpensive; offering good value.', 3: 'Average prices; mid-range budgets fit.', 4: 'Noticeably expensive; plan extra funds.', 5: 'Luxury-level pricing across the board.' },
  safety: { 1: 'Serious safety issues; high vigilance essential.', 2: 'Elevated risks; stay alert.', 3: 'Mixed safety; standard precautions suffice.', 4: 'Low crime rates; but be vigilant.', 5: 'Very safe; incidents are rare.' },
  transport: { 1: 'Sparse routes and infrequent service.', 2: 'Limited network; delays common.', 3: 'Functional core routes covering main areas.', 4: 'Reliable, wide-reaching system.', 5: 'Comprehensive, frequent, and wide reaching.' },
  accessibility: { 1: 'Limited; challenging for mobility needs.', 2: 'Some areas may be hard to reach.', 3: 'Key sights have basic accommodations.', 4: 'Most venues accessible with minor obstacles.', 5: 'Highly accessible with few obstacles.' },
  wifi: { 1: 'Barely usable connections; unreliable.', 2: 'Slow speeds; inconsistent connectivity.', 3: 'Stable enough for basic usage.', 4: 'Good speeds for calls and streaming.', 5: 'High-speed, reliable in most places.' },
  walkability: { 1: 'Sidewalks scarce; long distances between sights.', 2: 'Pedestrian areas limited and fragmented.', 3: 'Walkable hubs; transport needed for outlying areas.', 4: 'Comfortable to explore on foot and pedestrian friendly.', 5: 'Compact layout; walking is an excellent way to get around.' }
}

const CLIMATE_ICONS = {
  tropical: { Icon: FaSun, label: 'Tropical' },
  desert: { Icon: GiCactus, label: 'Desert' },
  savanna: { Icon: GiGrass, label: 'Savanna' },
  subtropical: { Icon: FaSun, label: 'Sub-tropical' },
  mediterranean: { Icon: FaLeaf, label: 'Mediterranean' },
  temperate: { Icon: FaLeaf, label: 'Temperate' },
  oceanic: { Icon: FaCloud, label: 'Oceanic' },
  continental: { Icon: FaCloud, label: 'Continental' },
  alpine: { Icon: GiMountainCave, label: 'Alpine' },
  polar: { Icon: FaSnowflake, label: 'Polar' }
}

const CLIMATE_DESCRIPTIONS = {
  tropical: 'Warm and humid year-round.',
  desert: 'Hot days, cool nights; very little rain.',
  savanna: 'Distinct wet and dry seasons.',
  subtropical: 'Hot summers, mild winters.',
  mediterranean: 'Hot, dry summers; mild, wet winters.',
  temperate: 'Moderate temperatures year-round.',
  oceanic: 'Mild with regular rainfall.',
  continental: 'Warm summers, cold winters.',
  alpine: 'Cool summers, snowy winters.',
  polar: 'Extremely cold all year.'
}

function SkeletonTiles() {
  return (
    <div className="summary-skeleton" role="status" aria-busy="true">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="summary-skel-tile" />
      ))}
      <div className="summary-skel-tags" />
    </div>
  )
}

function SummaryMetric({ type, Icon, label, rating, description }) {
  const [open, setOpen] = useState(false)
  const descId = useId()
  const toggle = () => setOpen(o => !o)
  const handleKey = e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle() } }
  return (
    <div
      className={`summary-metric ${open ? 'open' : ''} ${type}`}
      role="button"
      tabIndex="0"
      aria-expanded={open}
      aria-controls={descId}
      onClick={toggle}
      onKeyDown={handleKey}
    >
      {rating ? (
        <div className="icon-row" role="img" aria-label={`${label}: ${rating} out of 5`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Icon key={i} className={i < rating ? 'active' : 'inactive'} aria-hidden="true" />
          ))}
        </div>
      ) : (
        <Icon className="metric-icon" aria-hidden="true" />
      )}
      <div className="metric-label">
        {label}
        <span className="expand-icon" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </div>
      <p id={descId} className={`metric-description${open ? ' open' : ''}`} aria-hidden={!open}>
        {description}
      </p>
    </div>
  )
}

export default function SummaryPanel({ metrics = {}, tags = [], loading }) {
  const tagsRef = useRef(null)
  const listRef = useRef(null)
  const [firstRowCount, setFirstRowCount] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [tagsCollapsedHeight, setTagsCollapsedHeight] = useState(0)
  const [tagsOverflow, setTagsOverflow] = useState(false)

  const { values: data = {} } = metrics

  const ratingOrder = ['crowd', 'cost', 'walkability', 'transport', 'accessibility', 'wifi', 'safety']
  const beforeClimate = ['crowd', 'cost', 'walkability', 'transport']
  const afterClimate = ['accessibility', 'wifi', 'safety']
  const ratingKeys = ratingOrder.filter(k => Number.isInteger(data[k]) && data[k] >= 1 && data[k] <= 5)
  const hasClimate = data.climateType && CLIMATE_ICONS[data.climateType]

  const languages = []
  if (data.languagesPrimary) languages.push({ value: data.languagesPrimary, description: 'Typically spoken by locals.' })
  if (data.languagesSecondary) languages.push({ value: data.languagesSecondary, description: 'Often understood.' })

  const items = []
  if (!loading) {
    beforeClimate.forEach(key => {
      if (ratingKeys.includes(key)) {
        const { Icon, label } = RATING_ICONS[key]
        const value = data[key]
        const description = RATING_DESCRIPTIONS[key][value]
        items.push(
          <li key={key} className="summary-item">
            <SummaryMetric type="rating" Icon={Icon} label={label} rating={value} description={description} />
          </li>
        )
      }
    })
    if (hasClimate) {
      const { Icon, label } = CLIMATE_ICONS[data.climateType]
      const description = CLIMATE_DESCRIPTIONS[data.climateType]
      items.push(
        <li key="climate" className="summary-item">
          <SummaryMetric type="climate" Icon={Icon} label={label} description={description} />
        </li>
      )
    }
    afterClimate.forEach(key => {
      if (ratingKeys.includes(key)) {
        const { Icon, label } = RATING_ICONS[key]
        const value = data[key]
        const description = RATING_DESCRIPTIONS[key][value]
        items.push(
          <li key={key} className="summary-item">
            <SummaryMetric type="rating" Icon={Icon} label={label} rating={value} description={description} />
          </li>
        )
      }
    })
    languages.forEach(({ value, description }, i) => {
      items.push(
        <li key={`lang-${i}`} className="summary-item">
          <SummaryMetric type="language" Icon={FaLanguage} label={value} description={description} />
        </li>
      )
    })
    if (tags.length) {
      items.push(
        <li key="tags" ref={tagsRef} className="summary-item tags">
          {tags.map((t, i) => (
            <span key={t.id ?? i} className="tag-pill">
              {typeof t === 'string' ? t : t.description ?? ''}
            </span>
          ))}
        </li>
      )
    }
  }

  useLayoutEffect(() => {
    if (loading) return
    const calcRows = () => {
      if (!listRef.current) return
      const children = Array.from(listRef.current.children)
      if (!children.length) {
        setFirstRowCount(0)
        return
      }
      const top = children[0].offsetTop
      let count = 0
      for (const el of children) {
        if (el.offsetTop === top) count++
        else break
      }
      setFirstRowCount(count)
    }
    const calcTags = () => {
      if (!tagsRef.current) {
        setTagsOverflow(false)
        return
      }
      const parentRect = tagsRef.current.getBoundingClientRect()
      const spans = Array.from(tagsRef.current.querySelectorAll('.tag-pill'))
      if (!spans.length) {
        setTagsOverflow(false)
        return
      }
      const firstRect = spans[0].getBoundingClientRect()
      const collapsedH = firstRect.bottom - parentRect.top
      const fullH = tagsRef.current.scrollHeight
      setTagsCollapsedHeight(collapsedH)
      setTagsOverflow(fullH - collapsedH > 1)
    }
    calcRows()
    calcTags()
    window.addEventListener('resize', calcRows)
    window.addEventListener('resize', calcTags)
    return () => {
      window.removeEventListener('resize', calcRows)
      window.removeEventListener('resize', calcTags)
    }
  }, [items, loading])

  if (loading) return <SkeletonTiles />
  if (!items.length) return null

  const renderedItems = items.map((el, idx) => {
    const isTags = el.props.className && el.props.className.includes('tags')
    const hidden = !expanded && idx >= firstRowCount && !isTags
    const className = `${el.props.className}${hidden ? ' hidden' : ''}`
    const style =
      isTags && tagsOverflow && !expanded ? { maxHeight: tagsCollapsedHeight, overflow: 'hidden' } : undefined
    return React.cloneElement(el, { className, style })
  })

  return (
    <div className="summary-wrapper">
      <ul ref={listRef} className="summary-panel" aria-label="Destination summary">
        {renderedItems}
      </ul>
      {(items.length > firstRowCount || tagsOverflow) && (
        <button
          type="button"
          className="summary-toggle"
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
        >
          {expanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
        </button>
      )}
    </div>
  )
}
